import { useField } from 'formik'
import Styles from './GeneratedArchetypePreview.module.scss'
import { GeneratedArchetypePreviewProps } from './Types'
import { forwardRef, useEffect, useMemo } from 'react'
import { useGenerateDesignContentSync } from 'hooks/queries/workspaces/useGenerateDesignContentSync'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { observer } from 'mobx-react-lite'
import { Ref } from 'components/DesignContentPreview/Types'

const GeneratedArchetypePreview = observer(
  forwardRef<Ref, GeneratedArchetypePreviewProps>(
    ({ archetype, initialMediaFileId, onChange, width, height, onGenerate }, ref) => {
      const [{ value: topic }] = useField('topic')
      const [{ value: language }] = useField('language')
      const [{ value: brand_voice_id }] = useField('brand_voice_id')
      const [{ value: tone }] = useField('tone')
      const [{ value: brand_kit_id }] = useField('brand_kit_id')
      const [{ value: brand_visual_kit_id }] = useField('brand_visual_kit_id')
      const generateMutation = useGenerateDesignContentSync({
        retry: 3,
        onError: () => onGenerate?.('failed'),
        onSuccess: () => onGenerate?.('successful'),
      })
      const toneDep = JSON.stringify(tone)

      useEffect(() => {
        generateMutation.mutate({
          topic,
          language,
          brand_voice_id,
          tone,
          brand_kit_id,
          brand_visual_kit_id,
          apply_brand: true,
          initial_media_file_id: initialMediaFileId,
          archetype,
          width,
          height,
        })
      }, [archetype, topic, language, brand_voice_id, toneDep, brand_kit_id, brand_visual_kit_id]) // eslint-disable-line react-hooks/exhaustive-deps

      const request = generateMutation.data
      const generatedContent = request?.generated_content

      const content = useMemo(() => {
        if (generatedContent) {
          return JSON.parse(generatedContent)
        }
      }, [generatedContent])

      useEffect(() => {
        onChange(content)
      }, [content]) // eslint-disable-line react-hooks/exhaustive-deps

      return (
        <div className={Styles.root}>
          <LoadingShimmerer
            className={Styles.designPreviewContainer}
            show={generateMutation.isLoading}
            borderRadius={5}
            style={{ width: '100%', aspectRatio: 1080 / 1350 }}
          >
            {content && (
              <div className={Styles.designPreview} data-testid="archetype-preview">
                <DesignContentPreview content={content} ref={ref} />
              </div>
            )}
            {!content && generateMutation.error && (
              <div className={Styles.designPreview}>
                <div className={Styles.error} />
              </div>
            )}
          </LoadingShimmerer>
        </div>
      )
    },
  ),
)

export { GeneratedArchetypePreview }
